import React from 'react';
import * as Slider from '@radix-ui/react-slider';
import * as Accordion from '@radix-ui/react-accordion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ShareDialog from './ShareDialog';

// ── Custom Slider ────────────────────────────────────────────────────────────
// ... (omitting unchanged definitions inside replacement, I should use multi_replace for accuracy)
const NeonSlider = ({ id, label, value, min, max, step = 0.01, onChange, unit = '' }) => (
  <div className="flex flex-col gap-2">
    <div className="flex justify-between items-center">
      <label className="text-[10px] uppercase tracking-[0.18em] text-gray-400">{label}</label>
      <span className="text-[11px] font-mono text-[#00F0FF]">
        {typeof value === 'number' ? value.toFixed(2) : value}{unit}
      </span>
    </div>
    <Slider.Root
      data-testid={`slider-${id}`}
      className="relative flex items-center select-none touch-none w-full h-5"
      min={min} max={max} step={step} value={[value]}
      onValueChange={([v]) => onChange(v)}
    >
      <Slider.Track className="relative grow h-[2px] bg-gray-800">
        <Slider.Range className="absolute h-full bg-[#00F0FF]" />
      </Slider.Track>
      <Slider.Thumb
        className="block w-3 h-3 bg-[#00F0FF] focus:outline-none"
        style={{ boxShadow: '0 0 8px rgba(0,240,255,0.9)' }}
      />
    </Slider.Root>
  </div>
);

// ── Nipple Type Selector ─────────────────────────────────────────────────────
const NippleTypeBtn = ({ type, current, label, onChange }) => (
  <button
    data-testid={`nipple-type-${type}`}
    onClick={() => onChange(type)}
    className={`flex-1 py-2 text-[10px] uppercase tracking-[0.15em] border transition-all duration-150
      ${current === type
        ? 'border-[#00F0FF]/70 text-[#00F0FF] bg-[#00F0FF]/8'
        : 'border-gray-700/60 text-gray-500 hover:border-gray-500 hover:text-gray-300'
      }`}
    style={current === type ? { boxShadow: 'inset 0 0 10px rgba(0,240,255,0.07)' } : {}}
  >
    {label}
  </button>
);

// ── Skin Tone Swatches ───────────────────────────────────────────────────────
const SWATCH_COLORS = ['#F8D5A8', '#EBB97E', '#D49A5A', '#B97A3C', '#9A5C28', '#7A3E18', '#542810', '#340E06'];

const SkinSwatches = ({ value, onChange }) => (
  <div className="flex gap-[3px] mt-1">
    {SWATCH_COLORS.map((color, i) => {
      const t = i / (SWATCH_COLORS.length - 1);
      const selected = Math.abs(value - t) < 0.07;
      return (
        <button
          key={i}
          data-testid={`skin-swatch-${i}`}
          onClick={() => onChange(t)}
          style={{ backgroundColor: color }}
          className={`flex-1 h-5 transition-all duration-100 ${selected ? 'ring-1 ring-[#00F0FF] ring-offset-1 ring-offset-[#080B0F]' : ''}`}
        />
      );
    })}
  </div>
);

// ── Accordion Section ────────────────────────────────────────────────────────
const Section = ({ id, title, children }) => (
  <Accordion.Item value={id} className="border-b border-[#00F0FF]/12">
    <Accordion.Trigger
      data-testid={`section-${id}`}
      className="flex w-full items-center justify-between py-4 text-[10px] uppercase tracking-[0.2em] text-gray-400 hover:text-[#00F0FF] transition-colors group"
    >
      {title}
      <ChevronDown size={12} className="text-[#00F0FF]/50 transition-transform duration-200 group-data-[state=open]:rotate-180" />
    </Accordion.Trigger>
    <Accordion.Content className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
      <div className="flex flex-col gap-5 pb-5">
        {children}
      </div>
    </Accordion.Content>
  </Accordion.Item>
);

// ── Main Panel ───────────────────────────────────────────────────────────────
const ControlPanel = ({ params, isPanelOpen, onTogglePanel, onUpdate }) => {
  return (
    <aside
      data-testid="control-panel"
      className={`absolute bottom-0 right-0 w-full h-[50vh] md:top-16 md:bottom-auto md:w-[360px] md:h-[calc(100vh-4rem)] z-30 flex flex-col transition-transform duration-300 ease-in-out ${isPanelOpen ? 'translate-y-0 md:translate-x-0' : 'translate-y-full md:translate-y-0 md:translate-x-full'
        }`}
      style={{
        background: 'rgba(6,9,14,0.94)',
        borderLeft: '1px solid rgba(0,240,255,0.12)',
        borderTop: '1px solid rgba(0,240,255,0.12)',
        backdropFilter: 'blur(20px)',
      }}
    >
      <button
        onClick={onTogglePanel}
        className="absolute -top-[34px] right-4 md:top-[64px] md:-left-[34px] md:right-auto flex items-center justify-center w-[34px] h-[34px] bg-[#030405]/95 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/15 z-50 transition-colors md:border-r-0"
      >
        <ChevronRight size={18} className={`hidden md:block transition-transform duration-300 ${!isPanelOpen ? 'rotate-180' : ''}`} />
        <ChevronDown size={18} className={`md:hidden transition-transform duration-300 ${!isPanelOpen ? 'rotate-180' : ''}`} />
      </button>
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#00F0FF]/12 flex items-center gap-2">
        <div className="w-1.5 h-4" style={{ background: '#00F0FF', boxShadow: '0 0 8px #00F0FF' }} />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Parameters</span>
        <span className="ml-auto text-[9px] text-gray-600 font-mono tracking-widest">v1.0</span>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 custom-scrollbar">
        <Accordion.Root type="multiple" defaultValue={['geometry', 'nipple', 'material', 'details']}>

          <Section id="geometry" title="Geometry">
            <NeonSlider id="volume" label="Volume" value={params.volume} min={0.3} max={2.5} onChange={v => onUpdate('volume', v)} />
            <NeonSlider id="sag" label="Sag / Gravity" value={params.sag} min={0} max={0.5} onChange={v => onUpdate('sag', v)} />
            <NeonSlider id="lower-width" label="Lower Hemisphere Wdt." value={params.lowerWidth !== undefined ? params.lowerWidth : 0.5} min={0} max={1.0} onChange={v => onUpdate('lowerWidth', v)} />
          </Section>

          <Section id="nipple" title="Nipple & Areola">
            <NeonSlider id="nipple-plateau" label="Flat Top Diameter" value={params.nipplePlateau} min={0.2} max={0.9} onChange={v => onUpdate('nipplePlateau', v)} />
            <NeonSlider id="nipple-size" label="Nipple Size" value={params.nippleSize} min={0.1} max={0.8} onChange={v => onUpdate('nippleSize', v)} />
            <NeonSlider id="areol-radius" label="Areola Radius" value={params.areolRadius} min={0.1} max={1} onChange={v => onUpdate('areolRadius', v)} />
          </Section>

          <Section id="material" title="Material & Skin">
            <div className="flex flex-col gap-2">
              <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Skin Tone</span>
              <SkinSwatches value={params.skinTone} onChange={v => onUpdate('skinTone', v)} />
            </div>
            <NeonSlider id="skin-tone" label="Melanin Slider" value={params.skinTone} min={0} max={1} onChange={v => onUpdate('skinTone', v)} />
            <NeonSlider id="roughness" label="Skin Roughness" value={params.roughness} min={0.1} max={0.9} onChange={v => onUpdate('roughness', v)} />
            <NeonSlider id="sss" label="Subsurface Scat." value={params.sssStrength} min={0} max={1} onChange={v => onUpdate('sssStrength', v)} />
          </Section>

          <Section id="details" title="Detail Layers">
            <NeonSlider id="veins" label="Veins" value={params.veins} min={0} max={1} onChange={v => onUpdate('veins', v)} />
          </Section>

        </Accordion.Root>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-[#00F0FF]/10 space-y-3">
        <ShareDialog params={params} isPanel={true} />
        <p className="text-[9px] text-gray-700 uppercase tracking-widest leading-relaxed">
          For educational & artistic use only
        </p>
      </div>
    </aside>
  );
};

export default ControlPanel;
